import {Shader} from '../../index.js';

/**
 * Static Hypercube.js scene shader sources.
 * @example
 * const sourcefragment = ShaderScene.sourcefragment;
 * const sourcevertex = ShaderScene.sourcevertex;
 */
class ShaderScene extends Shader {

    /**
     * Stores the attributes needed by the shader program.
     * @type {Object<string, string>}
     * @public
     * @readonly
     * @static
     */
    static attributes = {

        'NORMAL': 'vec3',
        'POSITION': 'vec3'
    };

    /**
     * Stores the fragment shader source.
     * @type {string}
     * @public
     * @readonly
     * @static
     */
    static sourcefragment = (

        'precision highp float;' +

        'uniform vec3 colorfog;' +
        'uniform float biasdepth;' +
        'uniform float densityfog;' +
        'uniform float distancefogbegin;' +
        'uniform float distancefogmaximum;' +
        'uniform sampler2D texturedepth;' +

        'varying vec3 v_color;' +
        'varying vec4 v_depth;' +
        'varying vec3 v_light;' +
        'varying vec4 v_position;' +

        'bool shaded(in vec3 depth, in sampler2D texturedepth) {' +

            'return depth.x >= 0.0' +
            '&& depth.x <= 1.0' +
            '&& depth.y >= 0.0' +
            '&& depth.y <= 1.0' +
            '&& texture2D(texturedepth, depth.xy).r <= depth.z + biasdepth;' +
        '}' +

        'void main(void) {' +

            'float shadowratio = float(shaded(v_depth.xyz / v_depth.w, texturedepth));' +
            'float lightratio = 1.0 - shadowratio;' +
            'float fogratio = smoothstep(distancefogbegin, distancefogmaximum, length(v_position));' +

            'gl_FragColor = mix(vec4(v_color + (v_light * lightratio), 1.0), vec4(colorfog, 1.0), fogratio * densityfog);' +
        '}'
    );

    /**
     * Stores the vertex shader source.
     * @type {string}
     * @public
     * @readonly
     * @static
     */
    static sourcevertex = (

        'attribute vec3 NORMAL;' +
        'attribute vec3 POSITION;' +

        'uniform vec3 colorlightambiant;' +
        'uniform vec3 colorlightdirectional;' +
        'uniform vec3 colormaterial;' +
        'uniform vec3 directionlightdirectional;' +
        'uniform mat4 matrixdepth;' +
        'uniform mat4 projection;' +
        'uniform mat4 transformation;' +
        'uniform mat4 view;' +

        'varying vec3 v_color;' +
        'varying vec4 v_depth;' +
        'varying vec3 v_light;' +
        'varying vec4 v_position;' +

        'void main(void) {' +

            'float illuminationlightdirectional = max(0.0, dot(-normalize(directionlightdirectional), normalize(mat3(transformation) * NORMAL)));' +

            'v_color = colormaterial * colorlightambiant;' +
            'v_depth = matrixdepth * transformation * vec4(POSITION, 1.0);' +
            'v_light = colormaterial * colorlightdirectional * illuminationlightdirectional;' +
            'v_position = projection * view * transformation * vec4(POSITION, 1.0);' +

            'gl_Position = v_position;' +
        '}'
    );

    /**
     * Stores the uniforms needed by the shader program.
     * @type {Object<string, string>}
     * @public
     * @readonly
     * @static
     */
    static uniforms = {

        'biasdepth': 'float',
        'colorfog': 'vec3',
        'colorlightambiant': 'vec3',
        'colorlightdirectional': 'vec3',
        'colormaterial': 'vec3',
        'densityfog': 'float',
        'directionlightdirectional': 'vec3',
        'distancefogbegin': 'float',
        'distancefogmaximum': 'float',
        'matrixdepth': 'mat4',
        'projection': 'mat4',
        'texturedepth': 'sampler2D',
        'transformation': 'mat4',
        'view': 'mat4'
    };
}

export {

    ShaderScene
};

export default ShaderScene;
